import React, { useState } from 'react';
import { ArrowLeft, Building2, MapPin, Users, Church, UserCircle, Mail, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAssessmentStore } from '../store/assessmentStore';
import type { OrganizationData } from '../types';

interface OrganizationFormProps {
  onSubmit: () => void;
  onBack: () => void;
}

export default function OrganizationForm({ onSubmit, onBack }: OrganizationFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setOrganizationData, results, currentResponses } = useAssessmentStore();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Form data collection
    const formData = new FormData(e.currentTarget);
    const data = {
      name: formData.get('name') as string,
      location: formData.get('location') as string,
      size: formData.get('size') as string,
      denomination: formData.get('denomination') as string || null,
      contact_role: formData.get('contactRole') as string,
      contact_name: formData.get('contactName') as string,
      contact_email: formData.get('contactEmail') as string,
      challenge_description: formData.get('challengeDescription') as string || null
    };

    try {
      // Insert organization data into the database
      const { data: org, error: dbError } = await supabase
        .from('organizations')
        .insert(data)
        .select()
        .single();

      if (dbError) throw dbError;

      // Save assessment data to the database
      const assessmentData = {
        org_id: org.id,
        overall_score: results?.averageScore,
        category_scores: results?.categoryScores,
        responses: currentResponses
      };

      const { error: assessmentError } = await supabase
        .from('assessments')
        .insert(assessmentData);

      if (assessmentError) throw assessmentError;

      // Save organization data to the store
      const orgData: OrganizationData = {
        name: org.name,
        location: org.location,
        size: org.size,
        denomination: org.denomination || undefined,
        contactRole: org.contact_role,
        contactName: org.contact_name,
        contactEmail: org.contact_email,
      };

      setOrganizationData(orgData);
      onSubmit();
    } catch (err) {
      console.error('Error saving data:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to save assessment data. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <Building2 className="w-12 h-12 text-[#163B56]" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Organization Details</h2>
        <p className="text-gray-600">
          Please provide your organization's information to complete the assessment.
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <div className="relative">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Organization Name
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Building2 className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="name"
              id="name"
              required
              className="block w-full pl-10 py-3 rounded-lg border-gray-300 shadow-sm focus:border-[#163B56] focus:ring-[#163B56] focus:ring-opacity-50 transition-colors text-gray-900 placeholder-gray-400"
              placeholder="Enter organization name"
            />
          </div>
        </div>

        <div className="relative">
          <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MapPin className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="location"
              id="location"
              required
              className="block w-full pl-10 py-3 rounded-lg border-gray-300 shadow-sm focus:border-[#163B56] focus:ring-[#163B56] focus:ring-opacity-50 transition-colors text-gray-900 placeholder-gray-400"
              placeholder="City, State"
            />
          </div>
        </div>

        <div className="relative">
          <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-2">
            Church Size
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Users className="h-5 w-5 text-gray-400" />
            </div>
            <select
              name="size"
              id="size"
              required
              className="block w-full pl-10 py-3 rounded-lg border-gray-300 shadow-sm focus:border-[#163B56] focus:ring-[#163B56] focus:ring-opacity-50 transition-colors text-gray-900 appearance-none bg-white"
            >
              <option value="">Select size range</option>
              <option value="0-100">0-100 members</option>
              <option value="101-250">101-250 members</option>
              <option value="251-500">251-500 members</option>
              <option value="501-1000">501-1,000 members</option>
              <option value="1001+">1,000+ members</option>
            </select>
          </div>
        </div>

        <div className="relative">
          <label htmlFor="denomination" className="block text-sm font-medium text-gray-700 mb-2">
            Denomination (Optional)
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Church className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="denomination"
              id="denomination"
              className="block w-full pl-10 py-3 rounded-lg border-gray-300 shadow-sm focus:border-[#163B56] focus:ring-[#163B56] focus:ring-opacity-50 transition-colors text-gray-900 placeholder-gray-400"
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="relative">
          <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
            Contact Name
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <UserCircle className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="contactName"
              id="contactName"
              required
              className="block w-full pl-10 py-3 rounded-lg border-gray-300 shadow-sm focus:border-[#163B56] focus:ring-[#163B56] focus:ring-opacity-50 transition-colors text-gray-900 placeholder-gray-400"
              placeholder="Your full name"
            />
          </div>
        </div>

        <div className="relative">
          <label htmlFor="contactRole" className="block text-sm font-medium text-gray-700 mb-2">
            Your Role
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <UserCircle className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="contactRole"
              id="contactRole"
              required
              className="block w-full pl-10 py-3 rounded-lg border-gray-300 shadow-sm focus:border-[#163B56] focus:ring-[#163B56] focus:ring-opacity-50 transition-colors text-gray-900 placeholder-gray-400"
              placeholder="e.g., Pastor, Administrator"
            />
          </div>
        </div>

        <div className="md:col-span-2 relative">
          <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
            Contact Email
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              name="contactEmail"
              id="contactEmail"
              required
              className="block w-full pl-10 py-3 rounded-lg border-gray-300 shadow-sm focus:border-[#163B56] focus:ring-[#163B56] focus:ring-opacity-50 transition-colors text-gray-900 placeholder-gray-400"
              placeholder="your@email.com"
            />
          </div>
        </div>

        <div className="md:col-span-2 relative">
          <label htmlFor="challengeDescription" className="block text-sm font-medium text-gray-700 mb-2">
            What's your biggest operational challenge? (Optional)
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <div className="absolute top-3 left-3 pointer-events-none">
              <MessageSquare className="h-5 w-5 text-gray-400" />
            </div>
            <textarea
              name="challengeDescription"
              id="challengeDescription"
              rows={3}
              className="block w-full pl-10 py-3 rounded-lg border-gray-300 shadow-sm focus:border-[#163B56] focus:ring-[#163B56] focus:ring-opacity-50 transition-colors text-gray-900 placeholder-gray-400 resize-none min-h-[120px]"
              placeholder="Share your biggest operational challenge..."
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center px-6 py-3 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-[#163B56] hover:bg-[#0D2436] disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#163B56]"
        >
          {isSubmitting ? 'Saving...' : 'Complete Assessment'}
        </button>
      </div>
    </form>
  );
}